/**
 * Per-client model picks for the Playground lineup.
 *
 * One model per client, not one model for the whole lineup: a Claude column
 * and a ChatGPT column cannot share a model id, so the toolbar's single model
 * chip only ever spoke for the `direct` pseudo-client. Each real client in the
 * lineup carries its own pick, chosen inline on its column header and sent
 * with the dispatch (`models` on POST /api/sphinx/playground/runs).
 *
 * The picks are this browser's, like the lineup itself (`selected-host-
 * storage.ts`): one flat localStorage map, no project scoping — sphinx has a
 * single implicit project and the ten targets are global. A client with no
 * pick falls back to its configured model from the Clients page, then to the
 * provider-family default, which is what the server would have run anyway.
 */
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_MODEL_FOR_PROVIDER,
  SPHINX_MODELS,
  TARGET_PROVIDER,
  type SphinxModel,
  type TargetId,
} from "@/shared/sphinx";
import { modelsForClient } from "@/lib/client-models";

const STORAGE_KEY = "sphinx.playgroundClientModels";
const EVENT_NAME = "playground-client-models-changed";

export type PlaygroundClientModels = Partial<Record<string, SphinxModel>>;

const isSphinxModel = (value: unknown): value is SphinxModel =>
  typeof value === "string" && (SPHINX_MODELS as readonly string[]).includes(value);

/**
 * Models a client can actually be dispatched with. Family-pinned via the same
 * two tables the route's `modelOverridesFor` checks, so the dropdown never
 * offers a pick the server would silently drop. One implementation, shared
 * with Client Connectors' model picker (`@/lib/client-models`) — the two used
 * to disagree about the direct-API clients.
 */
export { modelsForClient };

/** Family default for a client — the model the server falls back to. */
export function defaultModelForClient(target: string): SphinxModel | null {
  const provider = (TARGET_PROVIDER as Record<string, "anthropic" | "openai">)[
    target
  ];
  return provider ? DEFAULT_MODEL_FOR_PROVIDER[provider] : null;
}

export function loadPlaygroundClientModels(): PlaygroundClientModels {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: PlaygroundClientModels = {};
    for (const [target, model] of Object.entries(parsed)) {
      // Drop a pick the client cannot run — the vocabulary can change under a
      // persisted map, and the server would silently ignore it anyway.
      if (
        isSphinxModel(model) &&
        (modelsForClient(target) as readonly string[]).includes(model)
      ) {
        out[target] = model;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function savePlaygroundClientModels(models: PlaygroundClientModels): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(models));
  } catch {
    // Private-mode / quota failures: the picks stay in React state for this tab.
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

/**
 * The model a client runs this send: its inline pick, else the model it is
 * configured with on the Clients page, else the provider-family default.
 */
export function resolvePlaygroundClientModel(
  target: string,
  picks: PlaygroundClientModels,
  configuredModelId?: string | null
): SphinxModel | null {
  const pick = picks[target];
  if (pick) return pick;
  const allowed = modelsForClient(target) as readonly string[];
  if (configuredModelId && allowed.includes(configuredModelId)) {
    return configuredModelId as SphinxModel;
  }
  return defaultModelForClient(target);
}

/** The `models` map for a dispatch: every real client in the lineup, resolved. */
export function dispatchModelsFor(
  targets: readonly TargetId[],
  picks: PlaygroundClientModels,
  configuredModelId: (target: TargetId) => string | null | undefined
): Partial<Record<TargetId, SphinxModel>> {
  const out: Partial<Record<TargetId, SphinxModel>> = {};
  for (const target of targets) {
    const model = resolvePlaygroundClientModel(
      target,
      picks,
      configuredModelId(target)
    );
    if (model) out[target] = model;
  }
  return out;
}

/** This browser's per-client picks, mirrored to localStorage. */
export function usePlaygroundClientModels(): {
  clientModels: PlaygroundClientModels;
  setClientModel: (target: string, model: SphinxModel) => void;
} {
  const [clientModels, setClientModels] = useState<PlaygroundClientModels>(
    loadPlaygroundClientModels
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reread = () => setClientModels(loadPlaygroundClientModels());
    window.addEventListener(EVENT_NAME, reread);
    window.addEventListener("storage", reread);
    return () => {
      window.removeEventListener(EVENT_NAME, reread);
      window.removeEventListener("storage", reread);
    };
  }, []);

  const setClientModel = useCallback((target: string, model: SphinxModel) => {
    setClientModels((prev) => {
      const next = { ...prev, [target]: model };
      savePlaygroundClientModels(next);
      return next;
    });
  }, []);

  return { clientModels, setClientModel };
}
